(() => {
  const normalizeText = (value = '') => value
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

  const comparablePath = (value = '') => {
    try {
      return new URL(value, window.location.origin).pathname;
    } catch (error) {
      return value.split('?')[0];
    }
  };

  const versionValue = (value = '') => {
    try {
      return new URL(value, window.location.origin).searchParams.get('v') || '';
    } catch (error) {
      return '';
    }
  };

  const readMedia = (card) => {
    const dataNode = card.closest('.t4s-product')?.querySelector('.tyrot-variant-media-data');
    if (!dataNode) return [];
    try {
      return JSON.parse(dataNode.textContent).filter((item) => item && item.url);
    } catch (error) {
      return [];
    }
  };

  const readVariantData = (card) => {
    const dataNode = card.closest('.t4s-product')?.querySelector('.tyrot-variant-wizard-data');
    if (!dataNode) return { mapping: [], variants: [] };
    try {
      const data = JSON.parse(dataNode.textContent);
      return {
        mapping: Array.isArray(data.mapping) ? data.mapping : [],
        variants: Array.isArray(data.variants) ? data.variants : []
      };
    } catch (error) {
      return { mapping: [], variants: [] };
    }
  };

  const swatchesFor = (card) => Array.from(card.closest('.t4s-product')?.querySelectorAll('.t4s-pr-color__item[data-img]') || []);

  const mediaForVariant = (card, variantId) => {
    if (!variantId) return [];
    const media = readMedia(card);
    const { mapping } = readVariantData(card);
    const entry = mapping.find((item) => String(item.id) === String(variantId));
    if (!entry || !Array.isArray(entry.media)) return [];

    const mediaById = new Map(media.map((item) => [String(item.id), item]));
    return entry.media.map((id) => mediaById.get(String(id))).filter(Boolean);
  };

  const setSelectedVariant = (card, variant) => {
    if (!variant || !Array.isArray(variant.options)) return;
    card.dataset.tyrotSelectedOptions = JSON.stringify(variant.options);
    swatchesFor(card).forEach((swatch) => {
      const optionIndex = Number(swatch.dataset.optionIndex);
      const optionValue = swatch.dataset.optionValue || '';
      const selected = Number.isInteger(optionIndex) && String(variant.options[optionIndex]) === String(optionValue);
      swatch.classList.toggle('is-swatch--selected', selected);
    });
  };

  const defaultVariantFor = (card) => {
    const media = readMedia(card);
    const firstMediaId = media[0]?.id;
    const { mapping, variants } = readVariantData(card);
    if (!firstMediaId || !mapping.length || !variants.length) return null;

    const entry = mapping.find((item) => Array.isArray(item.media) && String(item.media[0]) === String(firstMediaId))
      || mapping.find((item) => Array.isArray(item.media) && item.media.some((id) => String(id) === String(firstMediaId)));
    if (!entry) return null;
    return variants.find((variant) => String(variant.id) === String(entry.id)) || null;
  };

  const variantForSwatch = (card, swatch) => {
    const { variants } = readVariantData(card);
    if (!swatch || !variants.length) return null;

    let selectedOptions = [];
    try {
      selectedOptions = JSON.parse(card.dataset.tyrotSelectedOptions || '[]');
    } catch (error) {
      selectedOptions = [];
    }

    if (!selectedOptions.length) {
      const defaultVariant = defaultVariantFor(card);
      selectedOptions = Array.isArray(defaultVariant?.options) ? [...defaultVariant.options] : [];
    }

    const optionIndex = Number(swatch.dataset.optionIndex);
    if (Number.isInteger(optionIndex) && optionIndex >= 0) {
      selectedOptions[optionIndex] = swatch.dataset.optionValue || '';
    }

    const exactVariant = variants.find((variant) =>
      Array.isArray(variant.options)
      && variant.options.length === selectedOptions.length
      && variant.options.every((value, index) => String(value) === String(selectedOptions[index]))
    );
    return exactVariant || variants.find((variant) => String(variant.id) === String(swatch.dataset.vid)) || null;
  };

  const legacyMediaForSwatch = (card, swatch) => {
    const media = readMedia(card);
    if (!media.length || !swatch) return media;

    const nameNode = swatch.querySelector('.t4s-pr-color__name');
    const optionName = normalizeText(nameNode?.textContent || swatch.getAttribute('data-tooltip') || '');
    const featuredUrl = swatch.dataset.img || '';
    const featuredVersion = versionValue(featuredUrl);
    const featuredPath = comparablePath(featuredUrl);

    let byAlt = [];
    if (optionName) {
      byAlt = media.filter((item) => {
        const alt = normalizeText(item.alt).replace(/^(color|design|pattern|style)\s+/, '');
        const filename = normalizeText(decodeURIComponent(comparablePath(item.url).split('/').pop() || ''));
        const isMultiWord = optionName.includes(' ');
        const altContainsPhrase = (' ' + alt + ' ').includes(' ' + optionName + ' ');
        const filenameContainsPhrase = (' ' + filename + ' ').includes(' ' + optionName + ' ');
        return alt === optionName
          || alt.startsWith(optionName + ' ')
          || filename.startsWith(optionName + ' ')
          || (isMultiWord && (altContainsPhrase || filenameContainsPhrase || filename.includes(optionName)));
      });
    }

    let byUploadGroup = [];
    if (featuredVersion) {
      const featuredTimestamp = Number(featuredVersion);
      byUploadGroup = media.filter((item) => {
        const itemVersion = versionValue(item.url);
        if (itemVersion === featuredVersion) return true;
        if (!featuredTimestamp || !Number(itemVersion)) return false;
        return Math.abs(Number(itemVersion) - featuredTimestamp) <= 2;
      });
    }

    if (byAlt.length > 1 || byUploadGroup.length > 1) {
      const combined = [...byAlt, ...byUploadGroup];
      return combined.filter((item, index) => combined.findIndex((candidate) => candidate.id === item.id) === index);
    }

    const swatches = swatchesFor(card);
    const anchors = swatches
      .map((item) => {
        const path = comparablePath(item.dataset.img || '');
        return { swatch: item, index: media.findIndex((medium) => comparablePath(medium.url) === path) };
      })
      .filter((anchor) => anchor.index >= 0)
      .sort((a, b) => a.index - b.index);

    const selectedAnchor = anchors.find((anchor) => anchor.swatch === swatch)
      || anchors.find((anchor) => comparablePath(anchor.swatch.dataset.img || '') === featuredPath);

    if (selectedAnchor) {
      const nextAnchor = anchors.find((anchor) => anchor.index > selectedAnchor.index);
      const grouped = media.slice(selectedAnchor.index, nextAnchor ? nextAnchor.index : media.length);
      if (grouped.length) return grouped;
    }

    const featured = media.find((item) => comparablePath(item.url) === featuredPath);
    return featured ? [featured] : media;
  };

  const mediaForSwatch = (card, swatch) => {
    const variant = variantForSwatch(card, swatch);
    if (variant) {
      const mappedMedia = mediaForVariant(card, variant.id);
      if (mappedMedia.length) {
        setSelectedVariant(card, variant);
        return mappedMedia;
      }
    }
    return legacyMediaForSwatch(card, swatch);
  };

  const mediaForCard = (card, swatch = null) => {
    const media = readMedia(card);
    if (!media.length) return [];
    if (swatch) return mediaForSwatch(card, swatch);

    const defaultVariant = defaultVariantFor(card);
    if (defaultVariant) {
      const mappedMedia = mediaForVariant(card, defaultVariant.id);
      if (mappedMedia.length) {
        setSelectedVariant(card, defaultVariant);
        const firstMedia = media[0];
        return [firstMedia, ...mappedMedia.filter((item) => String(item.id) !== String(firstMedia.id))];
      }
    }
    return [media[0]];
  };

  const updateSliderState = (card) => {
    const track = card.querySelector('.t4s-product-img');
    if (!track) return;
    const count = Number(card.dataset.tyrotSlideCount || 1);
    const index = Math.min(count - 1, Math.max(0, Math.round(track.scrollLeft / track.clientWidth)));
    const bar = card.querySelector('.tyrot-mobile-slide-progress > span');
    const controls = card.querySelector('.tyrot-desktop-slide-controls');
    if (bar) bar.style.transform = `translateX(${index * 100}%)`;
    if (controls) {
      controls.hidden = count <= 1;
      controls.querySelector('[data-tyrot-slide-prev]').disabled = index <= 0;
      controls.querySelector('[data-tyrot-slide-next]').disabled = index >= count - 1;
    }
  };

  const ensureDesktopControls = (card) => {
    if (card.querySelector('.tyrot-desktop-slide-controls')) return;
    const track = card.querySelector('.t4s-product-img');
    const controls = document.createElement('span');
    controls.className = 'tyrot-desktop-slide-controls';
    controls.innerHTML = `
      <button type="button" data-tyrot-slide-prev aria-label="Previous product image">‹</button>
      <button type="button" data-tyrot-slide-next aria-label="Next product image">›</button>
    `;
    card.appendChild(controls);

    const moveBy = (direction) => {
      const count = Number(card.dataset.tyrotSlideCount || 1);
      const width = Math.max(1, track.clientWidth);
      const current = Math.min(count - 1, Math.max(0, Math.round(track.scrollLeft / width)));
      const next = Math.min(count - 1, Math.max(0, current + direction));
      track.scrollTo({ left: next * width, behavior: 'smooth' });
    };

    controls.querySelector('[data-tyrot-slide-prev]').addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      moveBy(-1);
    });

    controls.querySelector('[data-tyrot-slide-next]').addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      moveBy(1);
    });
  };

  const sizedCardImage = (value, width) => {
    try {
      const url = new URL(value, location.origin);
      if (url.hostname === 'cdn.shopify.com' || url.pathname.startsWith('/cdn/shop/')) {
        url.searchParams.set('width', String(width));
      }
      return url.href;
    } catch (_) { return value; }
  };
  const loadCardImage = (image) => {
    if (!image.dataset.tyrotImageSrc) return;
    image.srcset = image.dataset.tyrotImageSrcset || '';
    image.src = image.dataset.tyrotImageSrc;
    delete image.dataset.tyrotImageSrc;
    delete image.dataset.tyrotImageSrcset;
  };
  const cardImageObserver = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      loadCardImage(entry.target);
      cardImageObserver.unobserve(entry.target);
    });
  }, {rootMargin:'250px 0px'}) : null;

  const renderSlides = (card, swatch) => {
    const track = card.querySelector('.t4s-product-img');
    const product = card.closest('.t4s-product');
    const title = product?.querySelector('.t4s-product-title > a')?.textContent.trim() || 'TYROT product';
    const slides = mediaForCard(card, swatch);
    if (!track || !slides.length) return;

    track.querySelectorAll('img').forEach(image => cardImageObserver?.unobserve(image));
    track.replaceChildren();
    slides.forEach((medium, index) => {
      const image = document.createElement('img');
      image.className = 'tyrot-mobile-variant-slide';
      image.sizes = Math.ceil(track.clientWidth || card.clientWidth || 240) + 'px';
      image.dataset.tyrotImageSrcset = [240, 360, 480, 720].map(width => sizedCardImage(medium.url, width) + ' ' + width + 'w').join(', ');
      image.dataset.tyrotImageSrc = sizedCardImage(medium.url, 480);
      image.alt = medium.alt || title;
      image.loading = 'lazy';
      image.decoding = 'async';
      image.width = 720;
      image.height = 900;
      track.appendChild(image);
      if (cardImageObserver) cardImageObserver.observe(image); else loadCardImage(image);
    });

    card.querySelector('.tyrot-mobile-slide-progress')?.remove();
    if (slides.length > 1) {
      const progress = document.createElement('span');
      progress.className = 'tyrot-mobile-slide-progress';
      progress.setAttribute('aria-hidden', 'true');
      const bar = document.createElement('span');
      bar.style.width = `${100 / slides.length}%`;
      progress.appendChild(bar);
      card.appendChild(progress);
    }

    track.scrollLeft = 0;
    card.dataset.tyrotSlideCount = String(slides.length);
    ensureDesktopControls(card);
    window.requestAnimationFrame(() => updateSliderState(card));
  };

  const enhanceCard = (card) => {
    if (card.dataset.tyrotMobileSlider === 'true') return;
    const track = card.querySelector('.t4s-product-img');
    if (!track || !card.closest('.t4s-product')?.querySelector('.tyrot-variant-media-data')) return;

    card.dataset.tyrotMobileSlider = 'true';
    card.classList.add('tyrot-mobile-slider-ready');
    renderSlides(card);

    const productCard = card.closest('.t4s-product');
    const supportsHoverPreview = window.matchMedia?.('(hover: hover) and (pointer: fine)').matches === true;

    const jumpToSlide = (index) => {
      const previousBehavior = track.style.scrollBehavior;
      track.style.scrollBehavior = 'auto';
      track.scrollLeft = Math.max(0, index) * track.clientWidth;
      if (previousBehavior) track.style.scrollBehavior = previousBehavior;
      else track.style.removeProperty('scroll-behavior');
      updateSliderState(card);
    };

    if (supportsHoverPreview && productCard) {
      productCard.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'mouse' || Number(card.dataset.tyrotSlideCount || 1) < 2) return;
        card.dataset.tyrotHoverPreview = 'true';
        jumpToSlide(1);
      });

      productCard.addEventListener('pointerleave', (event) => {
        if (event.pointerType !== 'mouse') return;
        card.dataset.tyrotHoverPreview = 'false';
        jumpToSlide(0);
      });
    }

    let ticking = false;
    track.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        updateSliderState(card);
        ticking = false;
      });
    }, { passive: true });

    const productLink = card.querySelector('.t4s-full-width-link');
    let pointerStart = null;
    let pointerMoved = false;

    const isTouchGesture = (event) =>
      event.type.startsWith('touch') || event.pointerType === 'touch' || event.pointerType === 'pen';

    const keepGalleryGestureLocal = (event) => {
      if (isTouchGesture(event)) event.stopPropagation();
    };

    ['touchstart', 'touchmove', 'touchend', 'touchcancel'].forEach((eventName) => {
      track.addEventListener(eventName, keepGalleryGestureLocal, { passive: true });
    });

    track.addEventListener('pointerdown', (event) => {
      keepGalleryGestureLocal(event);
      pointerStart = { x: event.clientX, y: event.clientY };
      pointerMoved = false;
    }, { passive: true });

    track.addEventListener('pointermove', (event) => {
      keepGalleryGestureLocal(event);
      if (!pointerStart) return;
      if (Math.abs(event.clientX - pointerStart.x) > 7 || Math.abs(event.clientY - pointerStart.y) > 7) pointerMoved = true;
    }, { passive: true });

    track.addEventListener('pointerup', (event) => {
      keepGalleryGestureLocal(event);
      if (!pointerMoved && productLink?.href) window.location.assign(productLink.href);
      pointerStart = null;
    }, { passive: true });

    track.addEventListener('pointercancel', (event) => {
      keepGalleryGestureLocal(event);
      pointerStart = null;
    }, { passive: true });
  };

  const pendingCardEnhancements = new WeakSet();
  const productCardObserver = document.body.classList.contains('template-product') && 'IntersectionObserver' in window
    ? new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          productCardObserver.unobserve(entry.target);
          enhanceCard(entry.target);
        });
      }, { rootMargin: '500px 0px' })
    : null;

  const enhanceProductCards = (root = document) => {
    const cards = new Set();
    const hasVariantGalleryData = (card) =>
      Boolean(card?.closest('.t4s-product')?.querySelector('.tyrot-variant-media-data'));

    const ancestorCard = root.closest ? root.closest('.t4s-product-inner') : null;
    if (hasVariantGalleryData(ancestorCard)) cards.add(ancestorCard);
    if (root.matches?.('.t4s-product-inner') && hasVariantGalleryData(root)) cards.add(root);
    root.querySelectorAll?.('.t4s-product-inner').forEach((card) => {
      if (hasVariantGalleryData(card)) cards.add(card);
    });
    cards.forEach(card => {
      if (!productCardObserver) { enhanceCard(card); return; }
      if (pendingCardEnhancements.has(card)) return;
      pendingCardEnhancements.add(card);
      productCardObserver.observe(card);
    });
  };

  const enhanceFaqSections = (root = document) => {
    const sections = new Set();
    if (root.matches?.('.collection-faq-section')) sections.add(root);
    root.querySelectorAll?.('.collection-faq-section').forEach((section) => sections.add(section));

    sections.forEach((section) => {
      if (section.dataset.tyrotFaqEnhanced === 'true') return;
      const itemsWrap = section.querySelector('.collection-faq__items');
      const items = Array.from(section.querySelectorAll('.collection-faq__item'));
      if (!itemsWrap || items.length <= 4) return;

      section.dataset.tyrotFaqEnhanced = 'true';
      section.classList.add('tyrot-faq-enhanced');
      if (!itemsWrap.id) itemsWrap.id = `tyrot-faq-items-${Math.random().toString(36).slice(2, 9)}`;

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'tyrot-faq-toggle';
      button.setAttribute('aria-controls', itemsWrap.id);
      button.setAttribute('aria-expanded', 'false');
      button.innerHTML = `<span>View all ${items.length} FAQs</span><span aria-hidden="true">+</span>`;
      itemsWrap.insertAdjacentElement('afterend', button);

      button.addEventListener('click', () => {
        const isExpanded = section.classList.toggle('is-expanded');
        button.setAttribute('aria-expanded', String(isExpanded));
        button.innerHTML = isExpanded
          ? '<span>Show fewer FAQs</span><span aria-hidden="true">−</span>'
          : `<span>View all ${items.length} FAQs</span><span aria-hidden="true">+</span>`;
        if (!isExpanded) items.slice(4).forEach((item) => item.removeAttribute('open'));
      });
    });
  };

  const start = () => {
    enhanceProductCards();
    enhanceFaqSections();
    window.setTimeout(() => enhanceProductCards(), 300);
    window.setTimeout(() => enhanceProductCards(), 1400);
    window.setTimeout(() => enhanceFaqSections(), 500);

    document.addEventListener('click', (event) => {
      const swatch = event.target.closest('.t4s-pr-color__item[data-img]');
      const card = swatch?.closest('.t4s-product')?.querySelector('.t4s-product-inner');
      if (!swatch || !card) return;
      window.setTimeout(() => renderSlides(card, swatch), 80);
      window.setTimeout(() => renderSlides(card, swatch), 260);
    });

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) {
          enhanceProductCards(node);
          enhanceFaqSections(node);
        }
      }));
    });
    observer.observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();

/* Display Dadao's actual average and count, without rounding stars to halves. */
(() => {
  const cache = new Map(), requested = new Set();
  let timer;
  function render(node, data) {
    const value = Number(data.average_star), count = Number(data.total);
    if (!Number.isFinite(value) || value < 0 || value > 5 || !Number.isInteger(count) || count < 1) return;
    const stars = node.querySelectorAll('.dd-review-star');
    const label = node.querySelector('.dd-review-count');
    if (stars.length !== 5 || !label) return;
    const text = Number(value.toFixed(2)) + ' (' + count + ')';
    if (label.textContent !== text) label.textContent = text;
    node.setAttribute('aria-label', value + ' out of 5, ' + count + ' reviews');
    stars.forEach((star, index) => {
      const base = star.querySelector('i:not(.dd-review-star-half)');
      const fill = star.querySelector('.dd-review-star-half');
      if (!base || !fill) return;
      base.classList.remove('dd-review-color-active');
      base.classList.add('dd-review-color-default');
      fill.classList.remove('dd-review-color-default');
      fill.classList.add('dd-review-color-active');
      fill.style.width = (Math.max(0, Math.min(1, value - index)) * 100).toFixed(2) + '%';
    });
  }
  function update() {
    const nodes = Array.from(document.querySelectorAll('.ddreview-star[product-id]'));
    const ids = [];
    nodes.forEach(node => {
      const id = node.getAttribute('product-id').split('_')[0];
      if (!/^\d+$/.test(id)) return;
      if (cache.has(id)) render(node, cache.get(id));
      else if (!requested.has(id)) { requested.add(id); ids.push(id); }
    });
    if (!ids.length) return;
    fetch('https://review.wsy400.com/apps/goods-comment/overview', {
      method: 'POST', headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({goodsIds: ids.join(',')})
    }).then(response => { if (!response.ok) throw new Error('Rating unavailable'); return response.json(); })
      .then(result => {
        if (String(result.code) !== '200' || !Array.isArray(result.data)) return;
        result.data.forEach(entry => Object.entries(entry).forEach(([id, data]) => cache.set(id, data)));
        update();
      }).catch(() => { /* Keep the native widget if the rating service is unavailable. */ });
  }
  function schedule() { clearTimeout(timer); timer = setTimeout(update, 100); }
  function start() {
    new MutationObserver(schedule).observe(document.body, {childList: true, subtree: true});
    update();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, {once: true});
  else start();
})();


